import React from "react";

import { InvestmentQuoteResponse } from "@features/investments/api";

type QuoteSummaryStripProps = {
  ticker?: string;
  quote: InvestmentQuoteResponse | null;
  loading: boolean;
  error: string;
  lastSuccessAt: string | null;
  restricted?: boolean;
  compact?: boolean;
  onRefresh?: () => void;
};

export function QuoteSummaryStrip({
  ticker,
  quote,
  loading,
  error,
  lastSuccessAt,
  restricted = false,
  compact = false,
  onRefresh,
}: QuoteSummaryStripProps): JSX.Element {
  const direction = restricted ? "flat" : quoteDirectionClass(quote?.change ?? null);
  const hasQuote = !restricted && quote?.price !== null && quote?.price !== undefined;
  const isStale = Boolean(error && hasQuote);

  let statusText = "Live quote updates every 60s while this page is visible";
  if (restricted) {
    statusText = "Live quote available only in Operator Mode.";
  } else if (loading && !hasQuote) {
    statusText = "Loading live quote...";
  } else if (!hasQuote && error) {
    statusText = "Live quote unavailable.";
  } else if (isStale) {
    statusText = `Stale quote (last update ${formatQuoteTime(lastSuccessAt || quote?.asOf || null)})`;
  } else if (quote?.asOf) {
    statusText = `As of ${formatQuoteTime(quote.asOf)}`;
  } else if (lastSuccessAt) {
    statusText = `Updated ${formatQuoteTime(lastSuccessAt)}`;
  }

  return (
    <section className={`quote-strip${compact ? " compact" : ""}${restricted ? " restricted" : ""}`}>
      <div className="quote-strip-main">
        <div className="quote-strip-symbol">{quote?.ticker || ticker || "-"}</div>
        <div className="quote-strip-price">{formatCurrencyNullable(quote?.price ?? null)}</div>
      </div>
      <div className={`quote-strip-change ${direction}`}>
        <span>{formatSignedCurrencyNullable(quote?.change ?? null)}</span>
        <span>{formatSignedPercentNullable(quote?.changePercent ?? null)}</span>
      </div>
      <div className={`quote-strip-status ${restricted ? "restricted" : ""} ${isStale ? "stale" : ""}`.trim()}>
        {statusText}
      </div>
      {!restricted && onRefresh && (
        <button type="button" className="quote-strip-refresh" onClick={onRefresh} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh quote"}
        </button>
      )}
    </section>
  );
}

function quoteDirectionClass(change: number | null): string {
  if (change === null || !Number.isFinite(change)) {
    return "flat";
  }
  if (change > 0) {
    return "up";
  }
  if (change < 0) {
    return "down";
  }
  return "flat";
}

function formatCurrency(value: number, currency = "USD"): string {
  if (!Number.isFinite(value)) {
    return "-";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatCurrencyNullable(value: number | null, currency = "USD"): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  return formatCurrency(value, currency);
}

function formatSignedCurrencyNullable(value: number | null, currency = "USD"): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  const abs = formatCurrency(Math.abs(value), currency);
  if (value > 0) {
    return `+${abs}`;
  }
  if (value < 0) {
    return `-${abs}`;
  }
  return abs;
}

function formatSignedPercentNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  if (value > 0) {
    return `+${value.toFixed(2)}%`;
  }
  if (value < 0) {
    return `${value.toFixed(2)}%`;
  }
  return `${value.toFixed(2)}%`;
}

function formatQuoteTime(isoValue: string | null): string {
  if (!isoValue) {
    return "unknown";
  }
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) {
    return "unknown";
  }
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
